import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Loader2, AlertTriangle, ExternalLink } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { enterSandbox, getSandboxEntryStatus } from '../../services/sandboxService';
import { LIVE_SANDBOX_TAB_URL } from '../../utils/environment';
import LanguagePicker from '../../components/LanguagePicker';
import flexbeeLogo from '../../assets/media/flexbee-no-bg.webp';
import {
  PageWrapper,
  LanguagePickerWrapper,
  FormCard,
  LogoContainer,
  FormTitle,
  FormSubtitle,
  ErrorMessage,
  GradientLinkButton,
} from '../login/styles';

// Test ortamına giriş (sandbox self-servis giriş; tasarım belgesi
// docs/superpowers/specs/2026-10-07-sandbox-self-servis-giris-design.md, K4).
//
// Canlı panelin "Test Ortamına Git" düğmesi bu sayfayı yeni sekmede açar:
//   https://test.flexbee.app/sandbox/enter#t=<geçiş belgesi>
// Belge # parçasındadır (sunucu günlüğüne ve Referer'a düşmez). Sayfa onu okur, HEMEN adresten siler
// (tarayıcı geçmişinde / ekran görüntüsünde kalmasın) ve POST /v1/sandbox/enter'e verir:
//   - ready        → sunucu oturum çerezini yazdı; oturum benimsenir, /integrations açılır;
//   - provisioning → test ortamı kuruluyor: "hazırlanıyor" ekranı, bilet ile 3 sn'de bir yoklama;
//   - hata         → hata ekranı + canlı panele dönüş düğmesi.
//
// TEK KULLANIMLIK BELGE: ikinci gönderim sandbox.tokenAlreadyUsed döner. StrictMode'un çift efekt
// çağrısında da istek İKİ KEZ gitmez (`started` ref'i). Yanıtı hangi efekt turunun işleyeceği
// `alive` ref'iyle çözülür — ilk turun temizliği yanıtı yutmaz, sayfa "giriliyor"da asılı kalmaz.
//
// Uygulama kabuğu (Layout) YOK: açık rota, giriş sayfasının kart/arka plan dili.

export const POLL_INTERVAL_MS = 3000;
export const PROVISIONING_TIMEOUT_MS = 10 * 60 * 1000;

// "#t=<belge>" → belge ya da null.
export function readHandoffToken(hash) {
  const raw = String(hash || '').replace(/^#/, '');
  if (!raw) return null;
  const token = (new URLSearchParams(raw).get('t') || '').trim();
  return token || null;
}

// Başarılı yanıt gövdesi → { kind: 'ready', user } | { kind: 'provisioning', ticket, companyName }
// | { kind: 'error', message }. Tanınmayan şekil hatadır (genel metin).
export function classifyEntryResponse(res) {
  const body = res?.data;
  if (!body?.status) return { kind: 'error', message: body?.message || null };
  const d = body.data;
  if (d?.state === 'ready' && d.user) return { kind: 'ready', user: d.user };
  if (d?.state === 'provisioning' && d.ticket) {
    return { kind: 'provisioning', ticket: d.ticket, companyName: d.companyName || '' };
  }
  return { kind: 'error', message: null };
}

// Reddedilen istek: yanıt yoksa ağ kopukluğu, varsa sunucunun hata anahtarı.
export function classifyEntryError(err) {
  if (!err?.response) return { kind: 'network' };
  return { kind: 'error', message: err.response.data?.message || null };
}

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  display: block;
  margin: 4px auto 20px;
  color: ${({ theme }) => theme.colors.secondary.main};

  @media (prefers-reduced-motion: no-preference) {
    animation: ${spin} 0.9s linear infinite;
  }
`;

const CompanyName = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: #fff;
  text-align: center;
  margin-bottom: 10px;
`;

const ErrorBox = styled(ErrorMessage)`
  align-items: flex-start;
  margin-bottom: 14px;
  line-height: 1.45;

  svg { flex-shrink: 0; margin-top: 1px; }
`;

const Hint = styled(FormSubtitle)`
  margin-bottom: 22px;
`;

export default function SandboxEnterPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { adoptSession, loading: authLoading } = useAuth();

  // entering | provisioning | ready | error
  const [view, setView] = useState({ kind: 'entering' });

  const started = useRef(false);
  const alive = useRef(false);
  const timer = useRef(null);
  const adopted = useRef(false);

  useEffect(() => {
    alive.current = true;

    if (!started.current) {
      started.current = true;

      const token = readHandoffToken(window.location.hash);
      // Belge adreste kalmasın — okunur okunmaz silinir (geçmiş kaydı da değişir: replace).
      if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);

      if (!token) {
        setView({ kind: 'error', message: 'sandbox.invalidToken' });
      } else {
        let ticket = null;
        let companyName = '';
        let provisioningSince = null;

        // Yardımcılar birbirini döngüsel çağırır (yanıt → yoklama → yanıt): bildirimler yukarı taşınır.
        function timedOut() {
          return provisioningSince !== null && Date.now() - provisioningSince >= PROVISIONING_TIMEOUT_MS;
        }

        function run(request) {
          return Promise.resolve(request)
            .then(classifyEntryResponse, classifyEntryError)
            .then(apply);
        }

        function poll() {
          timer.current = null;
          if (!alive.current) return;
          run(getSandboxEntryStatus(ticket));
        }

        function schedulePoll() {
          if (timedOut()) {
            setView({ kind: 'error', timeout: true });
            return;
          }
          timer.current = setTimeout(poll, POLL_INTERVAL_MS);
        }

        function apply(result) {
          if (!alive.current) return;   // sayfa kapandı
          if (result.kind === 'ready') {
            setView({ kind: 'ready', user: result.user });
            return;
          }
          if (result.kind === 'provisioning') {
            if (provisioningSince === null) provisioningSince = Date.now();
            ticket = result.ticket;
            companyName = result.companyName || companyName;
            setView({ kind: 'provisioning', companyName });
            schedulePoll();
            return;
          }
          // Yoklama sırasında geçici ağ kopukluğu: kurulum sürüyor, bilet geçerli — yoklama sürer
          // (zaman aşımı yine geçerlidir). İlk girişte ağ hatası ise hata ekranıdır.
          if (result.kind === 'network' && ticket) {
            schedulePoll();
            return;
          }
          setView({ kind: 'error', message: result.message || null });
        }

        run(enterSandbox(token));
      }
    }

    return () => {
      alive.current = false;
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, []);

  // Oturum, AuthProvider'ın ilk /auth/me denetimi bittikten SONRA benimsenir: o denetim oturumsuz
  // açılışta 401 ile kullanıcıyı null'a çeker; geç gelirse benimsenen oturumu silerdi.
  useEffect(() => {
    if (view.kind !== 'ready' || authLoading || adopted.current) return;
    adopted.current = true;
    adoptSession(view.user);
    history.replace('/integrations');
  }, [view, authLoading, adoptSession, history]);

  let body;
  if (view.kind === 'error') {
    let message = t('sandboxEnv.enter.genericError');
    if (view.timeout) {
      message = t('sandboxEnv.enter.timeout');
    } else if (view.message) {
      message = t(`responseErrors.${view.message}`, { defaultValue: message });
    }
    body = (
      <>
        <FormTitle>{t('sandboxEnv.enter.errorTitle')}</FormTitle>
        <ErrorBox role="alert" data-role="sandbox-enter-error">
          <AlertTriangle size={16} />
          <span>{message}</span>
        </ErrorBox>
        <Hint>{t('sandboxEnv.enter.errorHint')}</Hint>
        <GradientLinkButton as="a" href={LIVE_SANDBOX_TAB_URL} data-role="sandbox-enter-back">
          <ExternalLink size={18} />
          {t('sandboxEnv.enter.backToPanel')}
        </GradientLinkButton>
      </>
    );
  } else if (view.kind === 'provisioning') {
    body = (
      <div role="status" aria-live="polite" data-role="sandbox-enter-provisioning">
        <Spinner size={36} aria-hidden="true" />
        <FormTitle>{t('sandboxEnv.enter.provisioningTitle')}</FormTitle>
        {view.companyName && <CompanyName data-role="sandbox-enter-company">{view.companyName}</CompanyName>}
        <FormSubtitle>{t('sandboxEnv.enter.provisioningText')}</FormSubtitle>
      </div>
    );
  } else {
    body = (
      <div role="status" aria-live="polite" data-role="sandbox-enter-entering">
        <Spinner size={36} aria-hidden="true" />
        <FormTitle>{t('sandboxEnv.enter.enteringTitle')}</FormTitle>
        <FormSubtitle>{t('sandboxEnv.enter.enteringText')}</FormSubtitle>
      </div>
    );
  }

  return (
    <PageWrapper>
      <LanguagePickerWrapper>
        <LanguagePicker />
      </LanguagePickerWrapper>
      <FormCard>
        <LogoContainer>
          <img src={flexbeeLogo} alt="Flexbee" />
        </LogoContainer>
        {body}
      </FormCard>
    </PageWrapper>
  );
}
